// Utility functions for mobile browser detection and session handling

export const isMobileBrowser = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  const userAgent = window.navigator.userAgent.toLowerCase();
  const mobileKeywords = [
    'mobile', 'android', 'iphone', 'ipad', 'ipod', 
    'blackberry', 'windows phone', 'opera mini'
  ];
  
  return mobileKeywords.some(keyword => userAgent.includes(keyword));
};

export const isBraveBrowser = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  // Check for Brave browser
  return !!(window as any).navigator.brave || 
         (window as any).navigator.userAgent.includes('Brave');
};

export const isIOSDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  const userAgent = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(userAgent);
};

export const isPrivateBrowsingMode = async (): Promise<boolean> => {
  if (typeof window === 'undefined') return false;
  
  try {
    // Try to use localStorage
    const testKey = '__private_browsing_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return false;
  } catch (e) {
    // If localStorage fails, we might be in private browsing
    return true;
  }
};

export const getStorageQuota = async (): Promise<number> => {
  if (typeof navigator !== 'undefined' && 'storage' in navigator && 'estimate' in navigator.storage) {
    try {
      const estimate = await navigator.storage.estimate();
      return estimate.quota || 0;
    } catch (e) {
      return 0;
    }
  }
  return 0;
};

export const isLowStorage = async (): Promise<boolean> => {
  const quota = await getStorageQuota();
  const usage = await getStorageUsage();
  
  // Consider low storage if less than 10MB available
  return (quota - usage) < 10 * 1024 * 1024;
};

export const getStorageUsage = async (): Promise<number> => {
  if (typeof navigator !== 'undefined' && 'storage' in navigator && 'estimate' in navigator.storage) {
    try {
      const estimate = await navigator.storage.estimate();
      return estimate.usage || 0;
    } catch (e) {
      return 0;
    }
  }
  return 0;
};

export const clearOldStorage = (): void => {
  try {
    // Clear old session data that might be taking up space
    const keysToCheck = ['old_token', 'temp_data', 'cache_'];
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        // Check if key is old or temporary
        if (keysToCheck.some(check => key.includes(check))) {
          keysToRemove.push(key);
        }
        
        // Check for old timestamped keys
        if (key.includes('_timestamp_')) {
          const timestamp = localStorage.getItem(key);
          if (timestamp) {
            const age = Date.now() - parseInt(timestamp, 10);
            if (age > 7 * 24 * 60 * 60 * 1000) { // 7 days
              keysToRemove.push(key);
            }
          }
        }
      }
    }
    
    // Remove old keys
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });
    
    console.log(`Cleared ${keysToRemove.length} old storage items`);
  } catch (e) {
    console.error('Failed to clear old storage:', e);
  }
};